import React, { useContext } from "react";

import { UserContext } from "../../contexts";
import { Money } from "../../components";

const EMPTY_VALUE = "—";

const formatText = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE;
  }

  return value;
};

const InternalDetailRow = ({ label, value, children }) => (
  <div className="internal-details__item">
    <strong className="internal-details__label">{label}</strong>
    <div className="internal-details__value">{children ?? formatText(value)}</div>
  </div>
);

// PHP casts `null` to 0, so the API's `(int) $a === (int) $b` comparison treats
// "both sides unknown" as a match. Mirror that rather than a stricter null
// check — diverging would blank a card the API had just populated.
const storeKey = (value) => (value == null ? 0 : Number(value));

/**
 * Mirrors `$ownsListing` in the API (ListingController::get): an admin OF THE
 * STORE THAT OWNS THE LISTING, or an RC master admin. `is_admin` alone is not
 * ownership — every tenant store has admins.
 *
 * The API already nulls every internal field for non-owners (buying price,
 * supplier, serial, costs, private note), so this is not what keeps another
 * store's numbers secret — that gate is server-side and predates this. What it
 * fixes is the empty shell: a foreign-store admin used to get the full card
 * rendered as a column of "—", which reads as "this listing has no internal
 * data" rather than "this isn't yours".
 *
 * Keep this predicate IDENTICAL to the controller's, which compares against
 * TenantContext::currentStoreId() — the ACTIVE store (Phase 5 store-switcher),
 * not the raw store_id_m column — falling back to store_id_m only when no
 * active store is set, same as the backend falls back when no active store is
 * validated on the request. Comparing against store_id_m unconditionally (as
 * this used to) hid the card for a multi-store admin viewing a listing that
 * belongs to whichever store they'd switched INTO, since their home store
 * would no longer match.
 */
export const ownsListing = (user, listing, activeStoreId) => {
  if (!user?.is_admin) {
    return false;
  }

  if (user.is_master_admin) {
    return true;
  }

  const currentStoreId = activeStoreId ?? user.store_id_m;

  return storeKey(currentStoreId) === storeKey(listing?.store_id_m);
};

const InternalDetails = ({ listing }) => {
  const { user, activeStoreId } = useContext(UserContext);

  if (!ownsListing(user, listing, activeStoreId)) {
    return null;
  }

  return (
    <div className="internal-details">
      <h3 className="internal-details__title">Internal information</h3>

      <div className="internal-details__grid">
        <InternalDetailRow label="Listing status" value={listing?.status} />
        <InternalDetailRow label="Supplier" value={listing?.supplier} />
        <InternalDetailRow label="Serial number" value={listing?.serial_number} />
        <InternalDetailRow label="Buying price">
          {listing?.buying_price ? (
            <Money amount={listing.buying_price} currency={listing.buying_currency} />
          ) : (
            EMPTY_VALUE
          )}
        </InternalDetailRow>
        <InternalDetailRow label="Polishing">
          {listing?.polishing ? (
            <Money amount={listing.polishing} currency="gbp" />
          ) : (
            EMPTY_VALUE
          )}
        </InternalDetailRow>
        <InternalDetailRow label="Accessories">
          {listing?.accessories ? (
            <Money amount={listing.accessories} currency="gbp" />
          ) : (
            EMPTY_VALUE
          )}
        </InternalDetailRow>
        <InternalDetailRow label="Shipping">
          {listing?.shipping ? (
            <Money amount={listing.shipping} currency="gbp" />
          ) : (
            EMPTY_VALUE
          )}
        </InternalDetailRow>
        <InternalDetailRow label="Travel">
          {listing?.travel ? (
            <Money amount={listing.travel} currency="gbp" />
          ) : (
            EMPTY_VALUE
          )}
        </InternalDetailRow>
      </div>

      <div className="internal-details__comment">
        <InternalDetailRow
          label="Internal comment"
          value={listing?.private_note}
        />
      </div>
    </div>
  );
};

export default InternalDetails;
